import { isOptionGroupArray, Option, OptionGroup } from "./types";
import { getIconSizeValue } from "../Icon";

const GROUP_LABEL_HEIGHT = 29;
const MIN_OPTION_HEIGHT = 32;

/**
 * This functions calculates the total heigh of the Dropdown
 * options panel according to the number of options requested to
 * be displayed.
 *
 * @param {Option[] | OptionGroup[]} options The Dropdown options.
 * @param {number} maxOptionsToShow Number of options to show.
 * @return {number} Total height of the options panel.
 */

export const getOptionsMaxHeight = (
  maxOptionsToShow: number,
  options?: Option[] | OptionGroup[],
): number => {
  let height = 0;
  /**
   * We need to use a type guard to know if we are receiving an Option[]
   * or a OptionGroup[]. We need to count the groups headers in the final
   * size.
   */
  if (isOptionGroupArray(options as OptionGroup[])) {
    let result = 0;
    let rest = maxOptionsToShow;
    let group = options?.length || 0;
    /**
     * This part is more complex. We need to go from group to group until we
     * have the number of options to show. Each time we jump to the next group
     * we need to add a header (GROUP_LABEL_HEIGHT). For example:
     *
     * * 1st Group:        3 options.
     * * 2nd Group:        4 options.
     * * maxOptionsToShow: 5 options.
     *
     * The total height is: 1 head + 3 options + 1 head + 2 options.
     */
    while (rest > 0 && group > 0) {
      result += GROUP_LABEL_HEIGHT;
      for (
        let i = 0;
        i <
        (options as OptionGroup[])[(options?.length || 0) - group].options
          .length;
        i++
      ) {
        if (rest <= 0) continue;
        const size =
          getIconSizeValue(
            (options as OptionGroup[])[(options?.length || 0) - group]
              .options?.[i]?.iconSize ?? "small",
          ) +
          2 * 8;
        result += size < MIN_OPTION_HEIGHT ? MIN_OPTION_HEIGHT : size;
        rest--;
      }
      group--;
    }
    return result;
  } else {
    /**
     * If we only have Options is simple. We just need to add the icons size
     * to the padding (2 * 8) to have the final size. And if the icon is smaller
     * the the text, then set a MIN_OPTION_HEIGHT for the option.
     */
    for (let i = 0; i < maxOptionsToShow; i++) {
      const size =
        getIconSizeValue((options as Option[])?.[i]?.iconSize ?? "small") +
        2 * 8;
      height += size < MIN_OPTION_HEIGHT ? MIN_OPTION_HEIGHT : size;
    }
  }

  return height;
};
